import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useApi } from '@/hooks/useApi';
import { usePermissions } from '@/hooks/usePermissions';
import { signOutExplicitly } from '@/lib/authSession';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useClerk } from '@clerk/clerk-react';
import { ChevronDown, LogOut, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  COLLAPSIBLE_NAVIGATION_SECTIONS,
  deriveNavigationRoles,
  getAutoExpandedNavigation,
  getNavigationHomePath,
  getVisibleNavigationSections,
  isNavigationHrefActive,
  shouldShowNavigationUpgrade,
} from './navigation/index';

interface SectionHeaderProps {
  label: string;
  isExpandable?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}

function SectionHeader({ label, isExpandable, isExpanded, onToggle }: SectionHeaderProps) {
  if (isExpandable) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between mb-1 mt-4 px-3 first:mt-0 hover:bg-secondary/50 rounded-lg py-1.5 transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-primary/50">
          {label}
        </span>
        <ChevronDown
          className={cn('h-3 w-3 text-primary/40 transition-transform', isExpanded && 'rotate-180')}
        />
      </button>
    );
  }

  return (
    <div className="mb-1 mt-4 px-3 first:mt-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-primary/50">
        {label}
      </span>
    </div>
  );
}

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user, clearAuth, profiles } = useAuthStore();
  const { hasPermission, hasCoachFlag } = usePermissions();
  const { api } = useApi();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const sessionActiveRoles = useAuthStore((state) => state.sessionActiveRoles);

  const allUserRoles = useMemo(
    () =>
      deriveNavigationRoles({
        userRole: user?.role,
        profiles,
        sessionActiveRoles,
      }),
    [user?.role, profiles, sessionActiveRoles]
  );

  const visibleSections = useMemo(
    () =>
      getVisibleNavigationSections({
        roles: allUserRoles,
        profiles,
        sessionActiveRoles,
        surface: 'mobile',
        hasPermission,
        hasCoachFlag,
      }),
    [allUserRoles, profiles, sessionActiveRoles, hasPermission, hasCoachFlag]
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  };

  const handleLogout = async () => {
    await signOutExplicitly(signOut);
    clearAuth();
    navigate('/login');
    onClose();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: location.pathname intentionally closes the drawer after navigation
  useEffect(() => {
    onCloseRef.current();
  }, [location.pathname]);

  useEffect(() => {
    const autoExpanded = getAutoExpandedNavigation(location.pathname);
    setExpandedSections((previous) => ({ ...previous, ...autoExpanded }));
  }, [location.pathname]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(event) => event.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close menu"
      />

      <div className="fixed inset-y-0 left-0 w-72 bg-card shadow-xl animate-slide-in-right">
        <div className="flex h-28 items-center justify-between border-b border-border px-4">
          <div className="flex flex-1 items-center justify-center">
            <Link to={getNavigationHomePath(allUserRoles)} onClick={onClose}>
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-20 w-auto" />
            </Link>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto p-4">
          {visibleSections.map((section, sectionIndex) => {
            const isCollapsible = COLLAPSIBLE_NAVIGATION_SECTIONS.includes(section.id);
            const isSectionExpanded = expandedSections[section.id] ?? !isCollapsible;

            return (
              <div key={section.id}>
                <SectionHeader
                  label={section.label}
                  isExpandable={isCollapsible}
                  isExpanded={isSectionExpanded}
                  onToggle={() => toggleSection(section.id)}
                />

                {isSectionExpanded &&
                  section.items.map((item, itemIndex) => {
                    const Icon = item.icon;
                    const animationDelay = `${(sectionIndex * 3 + itemIndex) * 30}ms`;
                    const needsUpgrade = shouldShowNavigationUpgrade(item, profiles);

                    if (item.children) {
                      const isExpanded = expandedSections[item.id] || false;
                      const hasActiveChild = item.children.some((child) =>
                        isNavigationHrefActive(location.pathname, child.href)
                      );

                      return (
                        <div key={item.id}>
                          <button
                            type="button"
                            onClick={() => toggleSection(item.id)}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-all opacity-0 animate-fade-in',
                              hasActiveChild
                                ? 'bg-primary/10 text-primary'
                                : 'text-primary/70 hover:bg-secondary hover:text-primary'
                            )}
                            style={{ animationDelay }}
                          >
                            <Icon className="h-5 w-5" />
                            {item.label}
                            <ChevronDown
                              className={cn(
                                'ml-auto h-4 w-4 transition-transform',
                                isExpanded && 'rotate-180'
                              )}
                            />
                          </button>
                          {isExpanded && (
                            <div className="ml-4 mt-1 space-y-0.5 border-l border-border pl-4">
                              {item.children.map((child) => {
                                const ChildIcon = child.icon;
                                const isActive = isNavigationHrefActive(
                                  location.pathname,
                                  child.href
                                );

                                return (
                                  <Link
                                    key={child.id}
                                    to={child.href || '#'}
                                    className={cn(
                                      'flex items-center gap-3 rounded-lg px-3 py-2 text-[14px] font-medium transition-all',
                                      isActive
                                        ? 'bg-primary/10 text-primary'
                                        : 'text-primary/70 hover:bg-secondary hover:text-primary'
                                    )}
                                  >
                                    <ChildIcon className="h-4 w-4" />
                                    {child.label}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (needsUpgrade) {
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setShowUpgradeModal(true)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-all opacity-0 animate-fade-in',
                            'text-primary/70 hover:bg-secondary hover:text-primary'
                          )}
                          style={{ animationDelay }}
                        >
                          <Icon className="h-5 w-5" />
                          {item.label}
                        </button>
                      );
                    }

                    const isActive = isNavigationHrefActive(location.pathname, item.href);

                    return (
                      <Link
                        key={item.id}
                        to={item.href || '#'}
                        className={cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-all opacity-0 animate-fade-in',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-primary/70 hover:bg-secondary hover:text-primary'
                        )}
                        style={{ animationDelay }}
                      >
                        <Icon className="h-5 w-5" />
                        {item.label}
                      </Link>
                    );
                  })}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary">
              {(user?.username || user?.name)?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-medium">@{user?.username || 'Loading...'}</p>
              <p className="truncate text-xs text-muted-foreground">
                {user?.email || 'Syncing user data...'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>

      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Join as an Athlete</DialogTitle>
            <DialogDescription>
              My Hub is your personal workout dashboard. To access it, you need to be a member or
              individual subscriber.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-4">
            <Button
              disabled={subscribing}
              onClick={async () => {
                setSubscribing(true);
                try {
                  const response = await api.post<{ checkout_url: string }>(
                    '/profiles/add-individual',
                    {}
                  );
                  if (response.success && response.data?.checkout_url) {
                    window.location.href = response.data.checkout_url;
                  } else {
                    setSubscribing(false);
                  }
                } catch {
                  setSubscribing(false);
                }
              }}
              className="w-full"
            >
              {subscribing ? 'Redirecting to checkout…' : 'Subscribe as Individual Member'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Or wait for an invite to join a gym as a member
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
